import {
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { columns } from "../../utils/columns";
import { WaitingRoomView } from "src/api/types";
import { LIMIT, PAGE } from "src/constants/searchParams";
import { useSearchParams } from "react-router-dom";
import { ROWS_PER_PAGE } from "src/constants/rowPerPage";
import { ChangeEvent, useCallback, useMemo } from "react";
import TableHeaderCell from "src/shared/TableHeaderCell";
import WaitingRoomTableRow from "./components/WaitingRoomTableRow";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { useFilters } from "src/hooks/useFilters";

interface TableProps {
  rooms: WaitingRoomView[];
  total: number;
}

const WaitingRoomTable = ({ rooms, total }: TableProps) => {
  const [params, setParams] = useSearchParams();
  const [rawPage] = useFilters(PAGE, "0");
  const [rawLimit] = useFilters(LIMIT, "100");
  const { selected, setSelected } = useSelection();

  //170 header + filters
  //64 gaps
  //32 top and bottom padding
  const height = useMemo(() => window.innerHeight - 138 - 48 - 32, []);

  const page = rawPage ? parseInt(rawPage, 10) : 0;
  const limit = rawLimit ? parseInt(rawLimit, 10) : ROWS_PER_PAGE[0];

  const handleChangePage = (_: unknown, newPage: number) => {
    params.set(PAGE, newPage.toString());
    setParams(params);
  };

  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
    params.set(LIMIT, e.target.value);
    params.set(PAGE, "0");
    setParams(params);
  };
  const incompleteRooms = useMemo(
    () => rooms.filter((r) => !["COMPLETED", "CANCELED"].includes(r.status)),
    [rooms],
  );

  const handleSelectAll = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelected(incompleteRooms.map((room) => room.id));
      } else {
        setSelected([]);
      }
    },
    [setSelected, incompleteRooms],
  );

  return (
    <Stack>
      <TableContainer
        component={Paper}
        sx={{
          p: 0,
          height: height - 52,
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{ borderCollapse: "separate" }}
        >
          <TableHead
            sx={{
              borderRadius: "8px",
              border: "1px solid var(--teal, #569099)",
            }}
          >
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  aria-label="select all rooms"
                  onChange={handleSelectAll}
                  checked={
                    incompleteRooms.length > 0 &&
                    incompleteRooms.length === selected.length
                  }
                  indeterminate={
                    selected.length > 0 && selected.length < rooms.length
                  }
                />
              </TableCell>
              {columns?.map((column) => (
                <TableHeaderCell key={column.id} column={column} />
              ))}
              <TableCell
                padding="none"
                sortDirection={undefined}
                style={{
                  fontWeight: "bolder",
                }}
              >
                More Info
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rooms.map((row) => (
              <WaitingRoomTableRow key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        sx={{ mr: 10 }}
        component="div"
        count={total}
        page={page}
        rowsPerPage={limit}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE}
      />
    </Stack>
  );
};

export default WaitingRoomTable;
